import { Disclaimer, Spacer } from 'components/atoms';
import { DirektkaufDialog, Head } from 'components/molecules';
import { Layout, PlusTeaser, ProfileHeader } from 'components/profiles';
import { Funktionen, Netzwerk, Stammdaten, WeitereProfile } from 'content/personen';
import type { GetServerSideProps, NextPage } from 'next';
import React, { useEffect } from 'react';
import { ReferenzType } from 'types';
import { PersonFreeDto } from 'types/personen';
import { getCanonicalUrl } from 'utils';
import { getPersonMetaTagContent } from 'utils/functions/seo';
import { useDirektkaufTokenMemory, useTranslation } from 'utils/hooks';
import { useCheckForGisaUpdatesMutation } from 'utils/hooks/mutations';
import { getPersonServersideProps } from 'utils/profileStaticProps';

interface PersonenProfilPageProps
{
  person: PersonFreeDto;
}

const PersonenProfilPage: NextPage<PersonenProfilPageProps> = (props) => 
{
  const { person } = props;
  const { t } = useTranslation(['profiles', 'common']);

  useDirektkaufTokenMemory();
  
  const gisaMutation = useCheckForGisaUpdatesMutation();
  useEffect(() => gisaMutation.mutate({ type: 'person', id: person.id }), []);

  const meta = getPersonMetaTagContent(t, person);

  return (
    <>
    <Layout key={person.id}>

      <Head
        title={meta.title}
        description={meta.description}
      >
        <meta name="keywords" content={meta.keywords} />
        <link rel="canonical" href={`${getCanonicalUrl()}/p/${person.id}`} />
        {!person.isIndexed && <meta name="robots" content="noindex" />}
      </Head>

      
      <ProfileHeader
        title={person.hauptPersonenDaten.fullName}
        id={person.id}
        type={ReferenzType.NatürlichePerson}
        tabs={[
          {
            text: t('profiles:navigation.allgemeines'),
            href: 'allgemeines',
          },
          {
            text: t('profiles:navigation.funktionen'),
            href: 'funktionen',
            hidden: !person.leitungsorgane,
          },
          {
            text: t('profiles:navigation.kontrollorgan'),
            href: 'kontrollorgan',
            hidden: !person.kontrollorgane,
          },
          {
            text: t('profiles:navigation.beteiligungen'),
            href: 'netzwerk',
            hidden: Object.keys(person.netzwerk).length === 0,
          },
          {
            text: t('profiles:navigation.plusInhalte'),
            href: 'plus-inhalte',
          },
        ]}
      />
      <div style={{ position:'relative' }}>
        <dl>
          
          <Stammdaten person={person} />

          <Funktionen person={person} />

          <WeitereProfile person={person} />

          <Netzwerk person={person} />
        </dl>

        <PlusTeaser
          plusInhalte={person.premiumInfo}
        />

        <Spacer height={2} />

        <Disclaimer
          text={t('profiles:personen.disclaimer')}
        />
      </div>
      <DirektkaufDialog id={person.id} type={ReferenzType.NatürlichePerson} />

    </Layout>
    </>
  );
};

export default PersonenProfilPage;

export const getServerSideProps: GetServerSideProps = async (ctx) =>
{
  const props = await getPersonServersideProps(ctx, false);
  return props;
};
